import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/lib/stores/auth.store';
import { userService, type PlusOneProfile, type CreatePlusOneProfileData } from '@/lib/api/user.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, UtensilsCrossed, Accessibility, Phone, Save, X, Users, Plus, Pencil, Trash2, MessageCircle } from 'lucide-react';
import { DIETARY_OPTIONS, type UserPreferences } from '@/types/auth.types';
import { useConversation } from '@/contexts/ConversationContext';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';

export const Profile: React.FC = () => {
  const { t } = useTranslation(['profile', 'common']);
  const { user, setUser } = useAuthStore();
  const { openQuickMessage } = useConversation();
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingPrefs, setIsEditingPrefs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    nickname: user?.nickname || '',
  });

  const [preferences, setPreferences] = useState<UserPreferences>({
    dietary_restrictions: [],
    dietary_notes: '',
    accessibility_needs: '',
    emergency_contact_name: '',
    emergency_contact_phone: ''
  });

  // Plus-one profiles state (FR-9.2)
  const [plusOneProfiles, setPlusOneProfiles] = useState<PlusOneProfile[]>([]);
  const [plusOneLoading, setPlusOneLoading] = useState(false);
  const [showAddPlusOne, setShowAddPlusOne] = useState(false);
  const [editingPlusOneId, setEditingPlusOneId] = useState<number | null>(null);
  const [plusOneForm, setPlusOneForm] = useState<CreatePlusOneProfileData>({ name: '', email: '' });

  // Load preferences and plus-one profiles on mount
  useEffect(() => {
    if (user?.id) {
      loadPreferences();
      loadPlusOneProfiles();
    }
  }, [user?.id]);

  const loadPreferences = async () => {
    if (!user?.id) return;
    try {
      const prefs = await userService.getPreferences(user.id);
      setPreferences(prefs);
    } catch (err) {
      console.error('Failed to load preferences:', err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePrefsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setPreferences(prev => ({ ...prev, [name]: value }));
  };

  const toggleDietaryRestriction = (value: string) => {
    setPreferences(prev => ({
      ...prev,
      dietary_restrictions: prev.dietary_restrictions.includes(value)
        ? prev.dietary_restrictions.filter(r => r !== value)
        : [...prev.dietary_restrictions, value]
    }));
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const updatedUser = await userService.updateProfile(user.id, formData);
      setUser(updatedUser);
      setIsEditingProfile(false);
    } catch (err) {
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    if (!user) return;
    setPrefsLoading(true);
    setError(null);
    try {
      await userService.updatePreferences(user.id, preferences);
      setIsEditingPrefs(false);
    } catch (err) {
      setError('Failed to update preferences');
    } finally {
      setPrefsLoading(false);
    }
  };

  const handleCancelProfile = () => {
    setFormData({
      full_name: user?.full_name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      nickname: user?.nickname || '',
    });
    setIsEditingProfile(false);
  };

  const handleCancelPrefs = () => {
    loadPreferences();
    setIsEditingPrefs(false);
  };

  // Plus-one profile handlers (FR-9.2)
  const loadPlusOneProfiles = async () => {
    if (!user?.id) return;
    try {
      const profiles = await userService.getPlusOneProfiles(user.id);
      setPlusOneProfiles(profiles);
    } catch (err) {
      console.error('Failed to load plus-one profiles:', err);
    }
  };

  const handleSavePlusOne = async () => {
    if (!user?.id || !plusOneForm.name.trim()) return;
    setPlusOneLoading(true);
    try {
      if (editingPlusOneId) {
        await userService.updatePlusOneProfile(user.id, editingPlusOneId, plusOneForm);
      } else {
        await userService.createPlusOneProfile(user.id, plusOneForm);
      }
      await loadPlusOneProfiles();
      setPlusOneForm({ name: '', email: '' });
      setShowAddPlusOne(false);
      setEditingPlusOneId(null);
    } catch (err) {
      setError('Failed to save plus-one profile');
    } finally {
      setPlusOneLoading(false);
    }
  };

  const handleEditPlusOne = (profile: PlusOneProfile) => {
    setEditingPlusOneId(profile.id);
    setPlusOneForm({
      name: profile.name,
      email: profile.email || '',
    });
    setShowAddPlusOne(true);
  };

  const handleDeletePlusOne = async (profileId: number) => {
    if (!user?.id) return;
    try {
      await userService.deletePlusOneProfile(user.id, profileId);
      await loadPlusOneProfiles();
    } catch (err) {
      setError('Failed to delete plus-one profile');
    }
  };

  const handleCancelPlusOne = () => {
    setPlusOneForm({ name: '', email: '' });
    setShowAddPlusOne(false);
    setEditingPlusOneId(null);
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <PageContainer width="default" className="space-y-4">
      <AppBreadcrumbs />
      <div className="mb-4">
        <h1 className="text-xl font-bold">{t('profile:title')}</h1>
        <p className="text-xs text-muted-foreground">Manage your account and preferences</p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Personal Information */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-primary" />
            <h2 className="font-semibold text-sm">{t('profile:sections.personalInfo')}</h2>
          </div>
          {!isEditingProfile && (
            <Button variant="ghost" size="sm" onClick={() => setIsEditingProfile(true)}>
              {t('common:actions.edit')}
            </Button>
          )}
        </div>

        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t('profile:fields.username')}</Label>
              <Input value={user.username} disabled className="mt-1 bg-muted/50" compact />
            </div>
            <div>
              <Label className="text-xs">{t('profile:fields.fullName')}</Label>
              <Input
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                disabled={!isEditingProfile}
                className={`mt-1 ${!isEditingProfile ? 'bg-muted/50' : ''}`}
                compact
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t('profile:fields.email')}</Label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={!isEditingProfile}
                className={`mt-1 ${!isEditingProfile ? 'bg-muted/50' : ''}`}
                compact
              />
            </div>
            <div>
              <Label className="text-xs">{t('profile:fields.phone')}</Label>
              <Input
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={!isEditingProfile}
                placeholder="Enter phone"
                className={`mt-1 ${!isEditingProfile ? 'bg-muted/50' : ''}`}
                compact
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">{t('profile:fields.nickname')}</Label>
            <Input
              name="nickname"
              value={formData.nickname}
              onChange={handleInputChange}
              disabled={!isEditingProfile}
              placeholder="Your display name (e.g., AH)"
              className={`mt-1 ${!isEditingProfile ? 'bg-muted/50' : ''}`}
              compact
              maxLength={50}
            />
          </div>
        </div>

        {isEditingProfile && (
          <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
            <Button variant="ghost" size="sm" onClick={handleCancelProfile}>
              <X className="h-3 w-3 mr-1" />{t('common:actions.cancel')}
            </Button>
            <Button size="sm" onClick={handleSaveProfile} disabled={loading}>
              <Save className="h-3 w-3 mr-1" />{loading ? t('common:status.saving') : t('common:actions.save')}
            </Button>
          </div>
        )}
      </div>

      {/* Dietary & Accessibility Preferences */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <UtensilsCrossed className="h-4 w-4 text-orange-500" />
            <h2 className="font-semibold text-sm">Dietary & Accessibility</h2>
          </div>
          {!isEditingPrefs && (
            <Button variant="ghost" size="sm" onClick={() => setIsEditingPrefs(true)}>
              {t('common:actions.edit')}
            </Button>
          )}
        </div>

        <p className="text-xs text-muted-foreground mb-3">
          This info helps event organizers accommodate your needs
        </p>

        {/* Dietary Restrictions */}
        <div className="mb-4">
          <Label className="text-xs font-medium">Dietary Restrictions</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {DIETARY_OPTIONS.map(option => (
              <button
                key={option.value}
                type="button"
                disabled={!isEditingPrefs}
                onClick={() => toggleDietaryRestriction(option.value)}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  preferences.dietary_restrictions.includes(option.value)
                    ? 'bg-orange-100 border-orange-300 text-orange-700'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                } ${!isEditingPrefs ? 'cursor-default opacity-70' : 'cursor-pointer'}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Dietary Notes */}
        <div className="mb-4">
          <Label className="text-xs font-medium">Additional Dietary Notes</Label>
          <textarea
            name="dietary_notes"
            value={preferences.dietary_notes || ''}
            onChange={handlePrefsChange}
            disabled={!isEditingPrefs}
            placeholder="Any specific allergies or dietary requirements..."
            className={`mt-1 w-full px-3 py-2 text-sm rounded-md border resize-none ${
              !isEditingPrefs ? 'bg-muted/50' : 'bg-background'
            } focus:ring-2 focus:ring-ring`}
            rows={2}
          />
        </div>

        {/* Accessibility Needs */}
        <div className="mb-4">
          <div className="flex items-center gap-1.5 mb-1">
            <Accessibility className="h-3.5 w-3.5 text-blue-500" />
            <Label className="text-xs font-medium">Accessibility Needs</Label>
          </div>
          <textarea
            name="accessibility_needs"
            value={preferences.accessibility_needs || ''}
            onChange={handlePrefsChange}
            disabled={!isEditingPrefs}
            placeholder="Wheelchair access, hearing assistance, etc..."
            className={`mt-1 w-full px-3 py-2 text-sm rounded-md border resize-none ${
              !isEditingPrefs ? 'bg-muted/50' : 'bg-background'
            } focus:ring-2 focus:ring-ring`}
            rows={2}
          />
        </div>

        {/* Emergency Contact */}
        <div className="pt-3 border-t">
          <div className="flex items-center gap-1.5 mb-2">
            <Phone className="h-3.5 w-3.5 text-red-500" />
            <Label className="text-xs font-medium">Emergency Contact (Optional)</Label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">Name</Label>
              <Input
                name="emergency_contact_name"
                value={preferences.emergency_contact_name || ''}
                onChange={handlePrefsChange}
                disabled={!isEditingPrefs}
                placeholder="Contact name"
                className={`mt-1 ${!isEditingPrefs ? 'bg-muted/50' : ''}`}
                compact
              />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Phone</Label>
              <Input
                name="emergency_contact_phone"
                value={preferences.emergency_contact_phone || ''}
                onChange={handlePrefsChange}
                disabled={!isEditingPrefs}
                placeholder="Phone number"
                className={`mt-1 ${!isEditingPrefs ? 'bg-muted/50' : ''}`}
                compact
              />
            </div>
          </div>
        </div>

        {isEditingPrefs && (
          <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
            <Button variant="ghost" size="sm" onClick={handleCancelPrefs}>
              <X className="h-3 w-3 mr-1" />{t('common:actions.cancel')}
            </Button>
            <Button size="sm" onClick={handleSavePreferences} disabled={prefsLoading}>
              <Save className="h-3 w-3 mr-1" />{prefsLoading ? t('common:status.saving') : 'Save Preferences'}
            </Button>
          </div>
        )}
      </div>

      {/* Saved Plus-Ones (FR-9.2) */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-violet-500" />
            <h2 className="font-semibold text-sm">Saved Plus-Ones</h2>
          </div>
          {!showAddPlusOne && (
            <Button variant="ghost" size="sm" onClick={() => setShowAddPlusOne(true)}>
              <Plus className="h-3 w-3 mr-1" />Add
            </Button>
          )}
        </div>

        <p className="text-xs text-muted-foreground mb-3">
          Save people you frequently bring to events for quick RSVP
        </p>

        {/* Add/Edit Form */}
        {showAddPlusOne && (
          <div className="border rounded-lg p-3 mb-3 bg-violet-50/50">
            <div className="grid gap-2">
              <div>
                <Label className="text-xs">Name *</Label>
                <Input
                  value={plusOneForm.name}
                  onChange={(e) => setPlusOneForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Full name"
                  className="mt-1"
                  compact
                />
              </div>
              <div>
                <Label className="text-xs">{t('profile:fields.email')}</Label>
                <Input
                  type="email"
                  value={plusOneForm.email || ''}
                  onChange={(e) => setPlusOneForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="plus-one@example.com"
                  className="mt-1"
                  compact
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3 pt-2 border-t">
              <Button variant="ghost" size="sm" onClick={handleCancelPlusOne}>
                <X className="h-3 w-3 mr-1" />{t('common:actions.cancel')}
              </Button>
              <Button size="sm" onClick={handleSavePlusOne} disabled={plusOneLoading || !plusOneForm.name.trim()}>
                <Save className="h-3 w-3 mr-1" />{plusOneLoading ? t('common:status.saving') : editingPlusOneId ? 'Update' : t('common:actions.save')}
              </Button>
            </div>
          </div>
        )}

        {/* Profiles List */}
        {plusOneProfiles.length === 0 && !showAddPlusOne ? (
          <p className="text-xs text-muted-foreground italic">No saved plus-ones yet. Add people you frequently bring to events.</p>
        ) : (
          <div className="space-y-2">
            {plusOneProfiles.map(profile => (
              <div key={profile.id} className="flex items-center justify-between p-2.5 rounded-lg border bg-gray-50/50 hover:bg-gray-100/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{profile.name}</span>
                    {profile.usage_count > 0 && (
                      <span className="text-[10px] text-muted-foreground bg-violet-100 px-1.5 py-0.5 rounded-full">
                        {profile.usage_count} event{profile.usage_count !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  {profile.email && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">{profile.email}</p>
                  )}
                </div>
                <div className="flex gap-1 ml-2">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEditPlusOne(profile)}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => handleDeletePlusOne(profile.id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Security - Compact */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-sm">Security</h2>
            <p className="text-xs text-muted-foreground">Password management coming soon</p>
          </div>
          <Button variant="outline" size="sm" disabled>
            Change Password
          </Button>
        </div>
      </div>

      {/* Quick Message - Open AI Assistant with user context */}
      {user?.id && (
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-sm">Quick Message</h2>
              <p className="text-xs text-muted-foreground">Send yourself a test message</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openQuickMessage({
                recipientId: user.id,
                recipientName: user.full_name,
                celebrationId: 1,
              })}
              className="gap-2"
            >
              <MessageCircle className="h-4 w-4" />
              Message
            </Button>
          </div>
        </div>
      )}
    </PageContainer>
  );
};
